import { Link } from "react-router-dom";
import { useGetStarted } from "@/components/GetStartedDialog";
import PageHead from "@/components/PageHead";
import { PILLARS } from "@/data/pillars";
import { ArrowRight } from "lucide-react";

const DIFFERENTIATORS = [
  { title: "15+ years of experience", body: "Building for enterprise, government, and small business — not learning on your project." },
  { title: "Senior developer, direct", body: "You work with the person actually writing the code. No account managers, no outsourced teams." },
  { title: "Accessibility-first", body: "WCAG 2.2 baked into every build. Government-grade compliance for every client." },
  { title: "Performance obsessed", body: "Sub-second load times, Core Web Vitals passing, images tuned to the byte." },
  { title: "CMS specialist", body: "Enterprise-grade Drupal, WordPress, and custom CMS work including 40-department municipal deployments." },
  { title: "Long-term partnership", body: "We stay in business with our clients. Retainers, roadmaps, and honest telemetry — not a launch-and-leave model." },
];

const BRAND_URL = "https://redkeyweb.design";

const HUB_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Services — Redkey Web Design",
  url: `${BRAND_URL}/services`,
  description:
    "Redkey Web Design services: custom web design & development, SEO & digital growth, AI automation, and Drupal, WordPress & government websites — for U.S. businesses and organizations.",
  hasPart: PILLARS.map((p) => ({
    "@type": "Service",
    name: p.name,
    url: `${BRAND_URL}/services/${p.slug}`,
    description: p.metaDescription,
    provider: { "@type": "Organization", name: "Redkey Web Design" },
    serviceType: p.keywords,
  })),
};

export default function Services() {
  const { open } = useGetStarted();

  return (
    <div data-testid="services-page">
      <PageHead
        title="Services — Custom Web Design, SEO, AI Automation & Drupal — Redkey Web Design"
        description="Redkey Web Design services: custom web design & development, technical SEO, AI automation, and Drupal / WordPress / government website development for U.S. businesses and organizations."
        canonical={`${BRAND_URL}/services`}
        jsonLd={HUB_JSON_LD}
      />

      {/* Hero */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-24 pb-14">
        <p className="eyebrow">§ Services · What We Do</p>
        <h1
          data-testid="services-title"
          className="font-display text-5xl md:text-7xl mt-6 max-w-[18ch]"
        >
          Four disciplines,{" "}
          <em className="not-italic text-brand-red">one team.</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-lg text-ink-soft">
          Redkey Web Design is a premium U.S. web design and development
          practice combining custom development, technical SEO, AI automation,
          and enterprise-grade Drupal / WordPress / accessibility work. We
          build websites that are fast, accessible, search-optimized, and
          engineered to help organizations grow — led by a senior developer
          with 15+ years of professional experience.
        </p>
      </section>

      {/* Pillar sections */}
      {PILLARS.map((p, idx) => {
        const Icon = p.Icon;
        return (
          <section
            key={p.id}
            id={p.id}
            data-testid={`pillar-${p.id}`}
            className={`border-t hairline ${idx % 2 === 1 ? "bg-paper-alt" : ""}`}
          >
            <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-5">
                <div className="flex items-baseline gap-4">
                  <span className="font-ui text-sm text-label tracking-widest">
                    {p.kicker}
                  </span>
                  <span className="h-px w-10 bg-[hsl(var(--line-strong))]" />
                  <Icon size={20} strokeWidth={1.75} className="text-ink" aria-hidden />
                </div>
                <h2 className="font-display text-4xl md:text-5xl mt-6">
                  {p.name}
                </h2>
                <p className="mt-4 font-display text-xl md:text-2xl text-brand-red-ink leading-snug">
                  {p.hubTagline}
                </p>
              </div>

              <div className="md:col-span-7 space-y-8">
                <p className="text-lg text-ink-soft leading-relaxed max-w-prose">
                  {p.intro}
                </p>

                <div>
                  <p className="eyebrow mb-4">What&rsquo;s included</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {p.included.map((item) => (
                      <li key={item} className="flex items-start gap-3 font-body text-ink">
                        <span className="text-brand-red mt-2 inline-block h-1.5 w-1.5 rounded-full shrink-0" aria-hidden />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* SR-only keyword list for SEO topical coverage */}
                <p className="sr-only">
                  Related services: {p.keywords.join(", ")}.
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/services/${p.slug}`}
                    data-testid={`pillar-deep-${p.id}`}
                    className="inline-flex items-center gap-3 bg-ink text-paper px-5 py-3 hover:bg-electric transition-colors font-ui"
                  >
                    Explore in depth
                    <ArrowRight size={16} />
                  </Link>
                  <button
                    type="button"
                    onClick={open}
                    data-testid={`pillar-cta-${p.id}`}
                    className="inline-flex items-center gap-3 border hairline-strong px-5 py-3 font-ui hover:border-ink transition-colors"
                  >
                    Discuss a project
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Why Redkey */}
      <section className="border-t hairline bg-ink text-paper">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-28">
          <p className="font-ui text-[11px] tracking-widest text-paper/60">
            § Why Redkey Web Design
          </p>
          <h2 className="font-display text-4xl md:text-6xl mt-6 max-w-[20ch] text-paper">
            The difference{" "}
            <em className="not-italic text-brand-red">a senior developer</em> makes.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.title} className="border-t border-paper/25 pt-5">
                <h3 className="font-display text-2xl md:text-3xl text-paper">
                  <span className="text-brand-red mr-2" aria-hidden>—</span>
                  {d.title}
                </h3>
                <p className="mt-3 font-body text-paper/85 leading-relaxed max-w-prose">
                  {d.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links to work */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="eyebrow">§ Proof</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 max-w-[20ch]">
                Custom builds you can actually visit.
              </h2>
            </div>
            <Link
              to="/work"
              className="inline-flex items-center gap-2 font-ui text-ink link-underline"
              data-testid="services-work-link"
            >
              See selected work <ArrowRight size={16} />
            </Link>
          </div>
          <p className="mt-6 max-w-prose text-lg text-ink-soft">
            Government sites serving hundreds of thousands of residents, an
            ADDY Award-winning municipal media platform, healthcare, home
            services, and luxury creative brands. Every one designed,
            developed, and shipped by hand.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-14 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-display text-3xl md:text-4xl max-w-[24ch]">
            Ready to make your website work as hard as you do?
          </p>
          <button
            type="button"
            onClick={open}
            data-testid="services-cta"
            className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
          >
            Get Started <span aria-hidden>→</span>
          </button>
        </div>
      </section>
    </div>
  );
}
